// =============================================================================
// features/crm/editOptions.ts — Opções dos selects de Cidade e Agente na edição
// do lead (F58-S08: CRM-04) e rótulo do agente no detalhe (CRM-07).
//
// O problema (CRM-04): a lista de cidades traz só as ATIVAS e a de agentes só
// os ATRIBUÍVEIS A QUEM EDITA. Quando o valor atual do lead não está na lista,
// o <select> não tem o que marcar e mostra "Selecione a cidade…" — parece que o
// lead não tem cidade, embora o Salvar não apague nada (só envia o que mudou).
// A correção é acrescentar o valor atual como opção rotulada. Nenhum payload
// muda: o valor da opção continua sendo o id que o lead já tinha.
//
// Sem React: testável sem DOM.
// =============================================================================

export interface SelectOption {
  value: string;
  label: string;
}

export interface CityOptionSource {
  id: string;
  name: string;
  state_uf: string;
}

export interface AgentOptionSource {
  id: string;
  display_name: string;
}

/** Rótulo do agente atual quando ele não está na lista de quem o usuário pode atribuir. */
export const AGENT_OUT_OF_LIST_LABEL = 'Agente atual (fora da sua lista)';

/**
 * Opções de cidade. Cidade atual do lead ausente da lista (inativa) entra como
 * "<nome> (inativa)" com o MESMO id que o lead já tem.
 *
 * `loading`: enquanto a lista não chegou, não dá para saber se a cidade está
 * ausente — não acrescenta nada (evita opção falsa que piscaria).
 */
export function buildCityOptions(
  cities: readonly CityOptionSource[],
  current: { id: string | null; name: string | null },
  loading: boolean,
): SelectOption[] {
  const options: SelectOption[] = [
    { value: '', label: loading ? 'Carregando cidades...' : 'Selecione a cidade...' },
    ...cities.map((c) => ({ value: c.id, label: `${c.name} — ${c.state_uf}` })),
  ];
  if (!loading && current.id && !cities.some((c) => c.id === current.id)) {
    options.splice(1, 0, { value: current.id, label: `${current.name ?? 'Cidade'} (inativa)` });
  }
  return options;
}

/**
 * Opções de agente. O agente atual fora da lista entra como opção com o seu id
 * — a API não devolve o nome de quem o usuário não pode atribuir, então o
 * rótulo é genérico (nunca um nome inventado).
 */
export function buildAgentOptions(
  agents: readonly AgentOptionSource[],
  currentAgentId: string | null,
  loading: boolean,
): SelectOption[] {
  const options: SelectOption[] = [
    { value: '', label: loading ? 'Carregando agentes...' : 'Sem agente' },
    ...agents.map((a) => ({ value: a.id, label: a.display_name })),
  ];
  if (!loading && currentAgentId && !agents.some((a) => a.id === currentAgentId)) {
    options.splice(1, 0, { value: currentAgentId, label: AGENT_OUT_OF_LIST_LABEL });
  }
  return options;
}

/** Detalhe: agente definido, mas o nome não está ao alcance de quem vê. */
export const AGENT_ASSIGNED_UNNAMED_LABEL = 'Agente atribuído';

/**
 * Texto do bloco "Agente responsável" no detalhe. `null` = ainda carregando a
 * lista (não afirma nada antes da hora). A lista de atribuíveis exige
 * `leads:write`: para quem só lê, ou quando o agente está fora dela, o nome
 * não existe no cliente e o texto é neutro — nunca "Sem agente" para um lead
 * que TEM agente.
 */
export function agentDisplayLabel(
  agentId: string | null,
  agents: readonly AgentOptionSource[],
  loading: boolean,
): string | null {
  if (!agentId) return 'Sem agente';
  const found = agents.find((a) => a.id === agentId);
  if (found) return found.display_name;
  return loading ? null : AGENT_ASSIGNED_UNNAMED_LABEL;
}
